import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { Loader2, X } from "lucide-react";
import api, { errorMessage } from "../services/api";
import {
  MAX_ADJUSTMENT_KOBO,
  MAX_ADJUSTMENT_MESSAGE,
  adjustmentFollowUp,
  adjustmentIntent,
  adjustmentKeyFor,
  formatKobo,
  parseNairaToKobo,
} from "../utils/money";

const REASON_MAX = 500;

const DIRECTIONS = [
  { id: "credit", label: "Credit (add money)" },
  { id: "debit", label: "Debit (take money out)" },
];

// Super admins only. The backend refuses everyone else with a 403.
// adjustKeys is a Map owned by the page, so a key outlives this modal (see adjustmentKeyFor).
const AdjustWalletModal = ({ userId, name, balanceKobo, adjustKeys, onClose, onAdjusted }) => {
  const [direction, setDirection] = useState("credit");
  const [amountText, setAmountText] = useState("");
  const [reason, setReason] = useState("");
  const [step, setStep] = useState("form");
  const [formError, setFormError] = useState(null);
  const [sendError, setSendError] = useState(null);
  // A refusal (4xx) will fail the same way again; only offer a retry when it may not have reached us.
  const [canRetry, setCanRetry] = useState(false);
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);
  const amountRef = useRef(null);
  const confirmRef = useRef(null);

  const parsed = parseNairaToKobo(amountText);
  const kobo = parsed.kobo;
  const remaining = REASON_MAX - reason.length;
  const hasBalance = Number.isFinite(balanceKobo);

  useEffect(() => {
    if (step === "form") amountRef.current?.focus();
    else confirmRef.current?.focus();
  }, [step]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape" && !sendingRef.current) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const close = () => {
    if (!sendingRef.current) onClose();
  };

  const review = (event) => {
    event.preventDefault();
    if (parsed.error) return setFormError(parsed.error);
    if (kobo > MAX_ADJUSTMENT_KOBO) return setFormError(MAX_ADJUSTMENT_MESSAGE);
    if (direction === "debit") {
      if (!hasBalance) return setFormError("The balance didn't load, so a debit can't be checked. Close and try again.");
      if (kobo > balanceKobo) {
        return setFormError(`That is more than their balance of ${formatKobo(balanceKobo)}.`);
      }
    }
    if (!reason.trim()) return setFormError("Add a reason. It is saved with the adjustment.");
    setFormError(null);
    setSendError(null);
    setStep("confirm");
  };

  const submit = async () => {
    if (sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);
    setSendError(null);
    const intent = adjustmentIntent(userId, direction, kobo);
    try {
      const amountKobo = direction === "debit" ? -kobo : kobo;
      const { data, status } = await api.post(`/v1/admin/wallets/${userId}/adjust`, {
        amountKobo,
        reason: reason.trim(),
        idempotencyKey: adjustmentKeyFor(adjustKeys, intent),
        source: "adjustment",
      });
      if (adjustmentFollowUp(status).clearKey) adjustKeys.delete(intent);
      const applied = formatKobo(data?.transaction?.amountKobo ?? kobo);
      const moved = direction === "debit" ? `debited from ${name}` : `credited to ${name}`;
      toast.success(
        data?.alreadyApplied
          ? `Already applied: ${applied} was already ${moved}.`
          : direction === "debit"
            ? `Debited ${applied} from ${name}`
            : `Credited ${applied} to ${name}`,
      );
      sendingRef.current = false;
      onAdjusted();
      onClose();
    } catch (error) {
      const status = error?.response?.status;
      // No answer or a 5xx: it may have gone through. The key is kept, so a retry is still safe.
      const followUp = adjustmentFollowUp(status);
      const details = error?.response?.data?.details;
      const message = Array.isArray(details) && details.length
        ? `${errorMessage(error, "Validation failed")}: ${details.join(". ")}.`
        : errorMessage(error, "Couldn't adjust the wallet. Please try again.");
      const sentence = /[.!?]$/.test(message) ? message : `${message}.`;
      setSendError(followUp.canRetry ? `${sentence} Trying again is safe: it won't be applied twice.` : message);
      setCanRetry(followUp.canRetry);
      if (followUp.reloadBalance) onAdjusted();
      sendingRef.current = false;
      setSending(false);
    }
  };

  const verb = direction === "debit" ? "Debit" : "Credit";
  const preposition = direction === "debit" ? "from" : "to";
  const newBalance = hasBalance && kobo ? balanceKobo + (direction === "debit" ? -kobo : kobo) : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={close} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="adjust-wallet-title"
        className="relative w-full max-w-md bg-white dark:bg-gray-900 rounded-xl shadow-xl p-6 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 id="adjust-wallet-title" className="text-lg font-semibold text-gray-900 dark:text-white">
              Adjust wallet
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {name} · current balance {formatKobo(balanceKobo)}
            </p>
          </div>
          <button
            type="button"
            onClick={close}
            disabled={sending}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {step === "form" ? (
          <form onSubmit={review} noValidate>
            <div role="group" aria-label="Type of adjustment" className="grid grid-cols-2 gap-2 mb-4">
              {DIRECTIONS.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  aria-pressed={direction === option.id}
                  onClick={() => {
                    setDirection(option.id);
                    setFormError(null);
                  }}
                  className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    direction === option.id
                      ? "bg-[#FF8C00] border-[#FF8C00] text-black"
                      : "border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>

            <label htmlFor="adjust-amount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Amount (₦)
            </label>
            <input
              id="adjust-amount"
              ref={amountRef}
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={amountText}
              onChange={(event) => {
                setAmountText(event.target.value);
                setFormError(null);
              }}
              placeholder="e.g. 1,500.50"
              className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent outline-none"
            />
            {direction === "debit" && (
              <p className="text-xs text-gray-500 mt-1">
                You can take out up to {formatKobo(balanceKobo)}.
              </p>
            )}

            <label htmlFor="adjust-reason" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mt-4 mb-1">
              Reason
            </label>
            <textarea
              id="adjust-reason"
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
                setFormError(null);
              }}
              rows={3}
              maxLength={REASON_MAX}
              aria-describedby="adjust-reason-count"
              placeholder="e.g. Refund for ride charged twice on 24 Oct."
              className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent outline-none resize-none"
            />
            <p id="adjust-reason-count" className="text-xs text-gray-500 mt-1 text-right">
              {remaining} characters left
            </p>

            {formError && (
              <p role="alert" className="text-sm text-red-600 dark:text-red-400 mt-3">
                {formError}
              </p>
            )}

            <div className="flex items-center justify-end gap-3 mt-5">
              <button
                type="button"
                onClick={close}
                className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-5 py-2 bg-[#FF8C00] text-black text-sm font-medium rounded-lg hover:bg-[#e67e00] transition-colors"
              >
                Review
              </button>
            </div>
          </form>
        ) : (
          <div>
            <div ref={confirmRef} tabIndex={-1} className="rounded-lg bg-gray-50 dark:bg-gray-800/60 p-4 outline-none">
              <p className="text-base font-semibold text-gray-900 dark:text-white">
                {verb} {formatKobo(kobo)} {preposition} {name}?
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-2 break-words">
                Reason: {reason.trim()}
              </p>
              {newBalance !== null && (
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">
                  Balance goes from {formatKobo(balanceKobo)} to {formatKobo(newBalance)}.
                </p>
              )}
            </div>

            {sendError && (
              <p role="alert" className="text-sm text-red-600 dark:text-red-400 mt-3">
                {sendError}
              </p>
            )}

            <div className="flex items-center justify-end gap-3 mt-5">
              <button
                type="button"
                onClick={() => setStep("form")}
                disabled={sending}
                className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
              >
                Back
              </button>
              {(!sendError || canRetry) && (
              <button
                type="button"
                onClick={submit}
                disabled={sending}
                className={`inline-flex items-center gap-2 px-5 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  direction === "debit" ? "bg-red-600 text-white hover:bg-red-700" : "bg-[#FF8C00] text-black hover:bg-[#e67e00]"
                }`}
              >
                {sending && <Loader2 className="h-4 w-4 animate-spin" />}
                {sending ? "Sending…" : sendError ? "Try again" : `Yes, ${verb.toLowerCase()} ${formatKobo(kobo)}`}
              </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdjustWalletModal;
